/**
 * useProgress – Student learning progress tracker
 * Stores progress in localStorage (instant) + Firestore (persistent sync)
 *
 * Firestore path: users/{uid}/progress/{subjectKey}   ← subcollection under users
 * Each doc stores: { [chapterId]: { lectures:{}, dpps:{}, tests:{} } }
 * Visible in Firebase Console: Firestore → users → {uid doc} → progress (subcollection)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { TUTORIAL_SUBJECTS } from '../config/tutorialCatalogue';

// ─── Helpers ────────────────────────────────────────────────────────────────

function cacheKey(uid, subjectKey) {
  return `ppx_progress_${uid}_${subjectKey}`;
}

function readCache(uid, subjectKey) {
  try {
    const raw = localStorage.getItem(cacheKey(uid, subjectKey));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeCache(uid, subjectKey, data) {
  try {
    localStorage.setItem(cacheKey(uid, subjectKey), JSON.stringify(data));
    // Notify listeners (e.g. useAllProgress) that progress changed
    try { window.dispatchEvent(new CustomEvent('ppx-progress')); } catch { /* SSR */ }
  } catch { /* storage full – silently ignore */ }
}

// Debounced Firestore write (fire-and-forget, won't block UI)
// Path: users/{uid}/progress/{subjectKey}  — one small doc per (user, subject)
// Keyed by uid+subjectKey (not subjectKey alone) so a second account using the
// same browser tab can never clobber an in-flight write for a different user.
let fsWriteTimers = {};
function debouncedFsWrite(uid, subjectKey, data) {
  const timerKey = `${uid}:${subjectKey}`;
  clearTimeout(fsWriteTimers[timerKey]);
  fsWriteTimers[timerKey] = setTimeout(async () => {
    try {
      const ref = doc(db, 'users', uid, 'progress', subjectKey);
      await setDoc(ref, data, { merge: true });
    } catch (err) {
      console.error('[useProgress] Firestore write failed:', err?.code, err?.message);
    }
  }, 800);
}

// ─── Main hook ───────────────────────────────────────────────────────────────

/**
 * @param {string} subjectKey  e.g. "iitjee-physics", "class11-physics"
 */
export function useProgress(subjectKey) {
  const { currentUser } = useAuth();
  const [progress, setProgress] = useState({});
  // Ignore our own optimistic writes echoing back so a stale merge can't
  // briefly "un-complete" something the user just marked done.
  const lastWrittenRef = useRef(null);

  // Load: localStorage first (instant), then Firestore is the live source of
  // truth via a real-time listener — so any device/browser/tab stays in sync
  // the moment progress changes anywhere, not just once at page load.
  useEffect(() => {
    if (!currentUser || !subjectKey) return;

    const cached = readCache(currentUser.uid, subjectKey);
    setProgress(cached);

    const unsub = onSnapshot(
      doc(db, 'users', currentUser.uid, 'progress', subjectKey),
      (snap) => {
        if (!snap.exists()) return;
        const subjectData = snap.data();
        if (!subjectData) return;
        if (JSON.stringify(subjectData) === lastWrittenRef.current) return; // our own echo
        setProgress((prev) => {
          const merged = deepMerge(prev, subjectData);
          writeCache(currentUser.uid, subjectKey, merged);
          return merged;
        });
      },
      () => { /* offline — local cache remains authoritative until reconnect */ }
    );
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, subjectKey]);

  /** Internal: update state, cache, and debounce Firestore write */
  const persist = useCallback(
    (updater) => {
      if (!currentUser) return;
      setProgress((prev) => {
        const next = updater(prev);
        lastWrittenRef.current = JSON.stringify(next);
        writeCache(currentUser.uid, subjectKey, next);
        debouncedFsWrite(currentUser.uid, subjectKey, next);
        return next;
      });
    },
    [currentUser, subjectKey]
  );

  // ─── Public API ────────────────────────────────────────────────────────────

  /** Call periodically while video plays. pct = 0-100 */
  const markLectureProgress = useCallback(
    (chapterId, lectureId, pct) => {
      persist((prev) => {
        const chapter = prev[chapterId] || {};
        const lectures = chapter.lectures || {};
        const existing = lectures[lectureId] || { pct: 0, done: false };
        const newPct = Math.max(existing.pct, Math.round(pct));
        const done = existing.done || newPct >= 80;
        return {
          ...prev,
          [chapterId]: {
            ...chapter,
            lectures: { ...lectures, [lectureId]: { pct: newPct, done } },
          },
        };
      });
    },
    [persist]
  );

  /** Call when DPP PDF is opened (downloaded) */
  const markDppOpened = useCallback(
    (chapterId, fileName) => {
      persist((prev) => {
        const chapter = prev[chapterId] || {};
        const dpps = chapter.dpps || {};
        const existing = dpps[fileName] || {};
        if (existing.opened) return prev; // no-op if already opened
        return {
          ...prev,
          [chapterId]: {
            ...chapter,
            dpps: { ...dpps, [fileName]: { ...existing, opened: true } },
          },
        };
      });
    },
    [persist]
  );

  /** Toggle "Mark Done" on a DPP */
  const toggleDppDone = useCallback(
    (chapterId, fileName) => {
      persist((prev) => {
        const chapter = prev[chapterId] || {};
        const dpps = chapter.dpps || {};
        const existing = dpps[fileName] || {};
        return {
          ...prev,
          [chapterId]: {
            ...chapter,
            dpps: { ...dpps, [fileName]: { ...existing, opened: true, done: !existing.done } },
          },
        };
      });
    },
    [persist]
  );

  /** Call when user navigates to a test */
  const markTestAttempted = useCallback(
    (chapterId, testId) => {
      persist((prev) => {
        const chapter = prev[chapterId] || {};
        const tests = chapter.tests || {};
        if (tests[testId]?.attempted) return prev; // no-op
        return {
          ...prev,
          [chapterId]: {
            ...chapter,
            tests: { ...tests, [testId]: { attempted: true, attemptedAt: Date.now() } },
          },
        };
      });
    },
    [persist]
  );

  /** Manually toggle lecture "done" — overrides / supplements auto-tracking */
  const toggleLectureDone = useCallback(
    (chapterId, lectureId) => {
      persist((prev) => {
        const chapter = prev[chapterId] || {};
        const lectures = chapter.lectures || {};
        const existing = lectures[lectureId] || { pct: 0, done: false };
        const newDone = !existing.done;
        return {
          ...prev,
          [chapterId]: {
            ...chapter,
            lectures: {
              ...lectures,
              [lectureId]: { pct: newDone ? 100 : 0, done: newDone },
            },
          },
        };
      });
    },
    [persist]
  );

  /** Manually toggle test "attempted" */
  const toggleTestAttempted = useCallback(
    (chapterId, testId) => {
      persist((prev) => {
        const chapter = prev[chapterId] || {};
        const tests = chapter.tests || {};
        const existing = tests[testId] || {};
        return {
          ...prev,
          [chapterId]: {
            ...chapter,
            tests: {
              ...tests,
              [testId]: { ...existing, attempted: !existing.attempted },
            },
          },
        };
      });
    },
    [persist]
  );

  /**
   * Returns chapter-level summary stats for a given chapter definition object
   * @param {string} chapterId
   * @param {{ lectures: [], dpps: [], tests: [] }} chapterDef
   */
  const getChapterStats = useCallback(
    (chapterId, chapterDef) => {
      const chProg = progress[chapterId] || {};
      const lecs = chProg.lectures || {};
      const dpps = chProg.dpps || {};
      const tests = chProg.tests || {};

      const totalLecs = (chapterDef.lectures || []).length;
      const doneLecs = (chapterDef.lectures || []).filter((l) => lecs[l.id]?.done).length;
      const startedLecs = (chapterDef.lectures || []).filter(
        (l) => lecs[l.id]?.pct > 0 && !lecs[l.id]?.done
      ).length;

      const totalDpps = (chapterDef.dpps || []).length;
      const doneDpps = (chapterDef.dpps || []).filter((d) => dpps[d.fileName]?.done).length;
      const openedDpps = (chapterDef.dpps || []).filter((d) => dpps[d.fileName]?.opened && !dpps[d.fileName]?.done).length;

      const totalTests = (chapterDef.tests || []).length;
      const doneTests = (chapterDef.tests || []).filter((t) => tests[t.testId]?.completed).length;

      const total = totalLecs + totalDpps + totalTests;
      const done = doneLecs + doneDpps + doneTests;
      const pct = total > 0 ? Math.round((done / total) * 100) : 0;

      // Status: "not-started" | "in-progress" | "completed"
      let status = 'not-started';
      if (done > 0 || startedLecs > 0 || openedDpps > 0) status = 'in-progress';
      if (total > 0 && done === total) status = 'completed';

      return {
        total, done, pct, status,
        totalLecs, doneLecs, startedLecs,
        totalDpps, doneDpps, openedDpps,
        totalTests, doneTests,
      };
    },
    [progress]
  );

  /**
   * Returns overall subject stats across all chapters
   * @param {Array} allChapters flat array of chapter definition objects
   */
  const getSubjectStats = useCallback(
    (allChapters) => {
      let total = 0, done = 0;
      (allChapters || []).forEach((ch) => {
        const s = getChapterStats(ch.id, ch);
        total += s.total;
        done += s.done;
      });
      return { total, done, pct: total > 0 ? Math.round((done / total) * 100) : 0 };
    },
    [getChapterStats]
  );

  return {
    progress,
    markLectureProgress,
    markDppOpened,
    toggleDppDone,
    markTestAttempted,
    toggleLectureDone,
    toggleTestAttempted,
    getChapterStats,
    getSubjectStats,
  };
}

// ─── All known subject keys (for Tutorials page progress display) ─────────────
const ALL_SUBJECT_KEYS = TUTORIAL_SUBJECTS.map(subject => subject.subjectKey);

// ─── Hook for Tutorials page + achievements ──────────────────────────────────
// Firestore is the single source of truth: one real-time onSnapshot listener
// per subject doc keeps this in sync across every browser/device instantly —
// not dependent on localStorage having been populated on this particular
// browser. localStorage is still written on every snapshot purely as an
// instant-paint cache for the next page load, never as the source of truth.
export function useAllProgress() {
  return useAllProgressState().allProgress;
}

export function useAllProgressState() {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;
  const [allProgress, setAllProgress] = useState({});
  const [loadedUid, setLoadedUid] = useState(null);

  useEffect(() => {
    setLoadedUid(null);
    if (!uid) { setAllProgress({}); return; }

    // Instant paint from whatever is cached locally, then each subject's
    // listener below will correct/update it the moment Firestore responds.
    const initial = {};
    ALL_SUBJECT_KEYS.forEach(key => {
      const cached = readCache(uid, key);
      if (Object.keys(cached).length > 0) initial[key] = cached;
    });
    setAllProgress(initial);

    const pending = new Set(ALL_SUBJECT_KEYS);
    const received = key => {
      pending.delete(key);
      if (pending.size === 0) setLoadedUid(uid);
    };
    const unsubs = ALL_SUBJECT_KEYS.map(key =>
      onSnapshot(
        doc(db, 'users', uid, 'progress', key),
        (snap) => {
          received(key);
          const data = snap.exists() ? snap.data() : null;
          if (data && Object.keys(data).length > 0) {
            writeCache(uid, key, data);
            setAllProgress(prev => ({ ...prev, [key]: data }));
          } else {
            // Doc deleted (e.g. unenroll) — drop it from cache + state too.
            try { localStorage.removeItem(cacheKey(uid, key)); } catch { /* ignore */ }
            setAllProgress(prev => {
              if (!(key in prev)) return prev;
              const next = { ...prev };
              delete next[key];
              return next;
            });
          }
        },
        error => {
          console.error('[useAllProgress] Could not load progress:', error);
          received(key);
        }
      )
    );
    return () => unsubs.forEach(u => u());
  }, [uid]);

  return { allProgress, loading: !!uid && loadedUid !== uid };
}

// ─── Deep merge utility ──────────────────────────────────────────────────────

function deepMerge(target, source) {
  const out = { ...target };
  for (const key of Object.keys(source || {})) {
    if (
      source[key] &&
      typeof source[key] === 'object' &&
      !Array.isArray(source[key]) &&
      out[key] &&
      typeof out[key] === 'object'
    ) {
      out[key] = deepMerge(out[key], source[key]);
    } else {
      out[key] = source[key];
    }
  }
  return out;
}
